import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import defer from 'lodash/defer';
import trim from 'lodash/trim';

import IconSend from 'components/Icons/IconSend';
import { getThemeProp, ITheme } from 'styles/theme';
import { AttachmentsConfig, Footer, MessageStatus } from 'types';
import { FooterText } from './FooterText/FooterText';
import {
  SFooter,
  SForm,
  SHiddenLabel,
  SInput,
  SFormInnerContainer,
  SAttachLabel,
  SAttachIcon,
  SEyeIcon,
  SEyeSlashIcon,
  SFilesContainer,
  SMaxFilesError,
  SNotificationWarningIcon,
  SRevealButton,
} from '../Chat.styled';
import { colors } from '@omilia/theme';
import { FilePreview } from 'components/FilePreview';
import { File } from 'types';
import { fileErrorMessages } from 'constants/fileErrors';
import { isIOS } from 'utils/detectOS';
import { isSafari } from 'utils/detectBrowser';
import { useTranslation } from '@omilia/providers';
import { KeyIcon } from '@omilia/icon';

interface ChatFooterProps {
  theme: ITheme;
  isOpen: boolean;
  isLoaded: boolean;
  isAsking: boolean;
  isStarting: boolean;
  isChatDisabled: boolean;
  isSensitiveInput?: boolean;
  hasError: boolean;
  footer?: Footer;
  withAgent?: boolean;
  isAgentConnecting?: boolean;
  attachmentsConfig: AttachmentsConfig;
  sendMessage: (args: { utterance: string; isSensitive?: boolean }) => void;
  sendFile: (files: File[], utterance: string) => void;
  sendTypingOn: () => void;
  sendTypingOff: () => void;
  uploadFile: () => void;
  resetFile: (fileName: string) => void;
  resetAllFiles: () => void;
  fileInputProps: any;
  selectedFiles: File[];
  fileWidth: number;
  maxFiles: number;
}

const INITIAL_INPUT_HEIGHT = 20;
const MIN_INPUT_HEIGHT = 5;

const ChatFooter: React.FC<ChatFooterProps> = ({
  theme,
  isLoaded,
  hasError,
  isOpen,
  isAsking,
  isStarting,
  isChatDisabled,
  isSensitiveInput = false,
  isAgentConnecting,
  footer,
  sendMessage,
  sendFile,
  fileInputProps,
  uploadFile,
  selectedFiles,
  resetFile,
  resetAllFiles,
  attachmentsConfig,
  fileWidth,
  maxFiles,
}) => {
  const [utterance, setUtterance] = useState('');
  const [isRevealed, setIsRevealed] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const { t } = useTranslation();

  const {
    attachmentsEnabled,
    attachmentsRichEnabled,
    allowedTypes,
    maxFileSize,
  } = attachmentsConfig;

  const files = useMemo(() => [...selectedFiles].reverse(), [selectedFiles]);

  const isInputDisabled = useMemo(() => {
    if (!isLoaded || hasError || isAsking || isStarting || isAgentConnecting) {
      return true;
    }
    if (isChatDisabled && files.length === 0) {
      return true;
    }
    return false;
  }, [
    isLoaded,
    hasError,
    isAsking,
    isStarting,
    isAgentConnecting,
    isChatDisabled,
    files.length,
  ]);

  const isAttachmentsDisabled = useMemo(
    () =>
      isSensitiveInput ||
      (!attachmentsEnabled && !attachmentsRichEnabled) ||
      hasError,
    [attachmentsEnabled, attachmentsRichEnabled, hasError, isSensitiveInput],
  );

  const validateFileType = (file: File) =>
    allowedTypes.some(type => type === file.type);
  const validateFileSize = (file: File) => file.size <= maxFileSize;

  const isSendButtonDisabled = useMemo(() => {
    if (isSensitiveInput) {
      return isInputDisabled || utterance.length === 0;
    }
    if (isAttachmentsDisabled) {
      return isInputDisabled || !utterance.trim();
    }
    return (
      isInputDisabled ||
      (files.length > 0 &&
        !files.every(file => validateFileSize(file) && validateFileType(file)))
    );
  }, [
    isAttachmentsDisabled,
    isInputDisabled,
    isSensitiveInput,
    files.length,
    utterance,
  ]);

  const sendButtonColor = useMemo(() => {
    const hasContent = utterance || files.length > 0;
    return hasContent && !isSendButtonDisabled
      ? getThemeProp('primaryColor', { theme })
      : colors.COLOR_GREY_600;
  }, [utterance, files.length, isSendButtonDisabled, theme]);

  const getFileError = (file: File): string => {
    if (!files.length) return '';

    const isFileTypeValid = validateFileType(file);
    const isFileSizeValid = validateFileSize(file);

    if (!isFileTypeValid) {
      return t(fileErrorMessages.FILE_TYPE);
    }
    if (!isFileSizeValid) {
      return t(fileErrorMessages.FILE_SIZE);
    }
    return '';
  };

  const adjustInputHeight = () => {
    if (!inputRef.current) return;

    const input = inputRef.current;
    if (input.scrollHeight > INITIAL_INPUT_HEIGHT) {
      input.style.height = `${MIN_INPUT_HEIGHT}px`;
      input.style.height = `${input.scrollHeight}px`;
    } else {
      input.style.height = `${INITIAL_INPUT_HEIGHT}px`;
    }
  };

  const resetInputHeight = () => {
    if (inputRef.current) {
      inputRef.current.style.height = `${INITIAL_INPUT_HEIGHT}px`;
    }
  };

  const focusInput = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const focusForm = useCallback(() => {
    if (formRef.current) {
      formRef.current.focus();
    }
  }, []);

  const submitMessage = (messageText: string) => {
    if (isSensitiveInput) {
      sendMessage({ utterance: messageText, isSensitive: true });
    } else {
      sendMessage({ utterance: messageText });
    }
    setUtterance('');
    setIsRevealed(false);
    resetInputHeight();
  };

  const submitFiles = (messageText = '') => {
    sendFile(files, messageText);
    resetAllFiles();
    setUtterance('');
    resetInputHeight();
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setUtterance(e.target.value);
    if (!isSensitiveInput) {
      adjustInputHeight();
    }
  };

  const toggleReveal = () => setIsRevealed(prev => !prev);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isSendButtonDisabled) {
        handleSubmit(e);
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isSensitiveInput) {
      // Preserve whitespace in passwords; never send a sensitive utterance with files.
      if (utterance.length > 0) {
        submitMessage(utterance);
      }
      return;
    }

    const trimmedUtterance = trim(utterance);

    if (files.length > 0) {
      submitFiles(trimmedUtterance);
    } else if (trimmedUtterance) {
      submitMessage(trimmedUtterance);
    }
  };

  const handleFileUpload = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!isAttachmentsDisabled) {
      uploadFile();
    }
  };

  // Effects
  useEffect(() => {
    if (isLoaded && isOpen && !hasError) {
      defer(focusInput);
    }
  }, [isLoaded, isOpen, hasError, utterance, focusInput]);

  useEffect(() => {
    if (files.length > 0) {
      focusForm();
    } else {
      focusInput();
    }
  }, [files.length, focusForm, focusInput]);

  useEffect(() => {
    if (hasError) {
      setUtterance('');
    }
  }, [hasError]);

  useEffect(() => {
    // Reset reveal state and clear in-flight text whenever sensitivity mode flips.
    setIsRevealed(false);
    setUtterance('');
    resetInputHeight();
    defer(focusInput);
  }, [isSensitiveInput]);

  return (
    <SFooter data-test="chat-footer">
      <SForm
        ref={formRef}
        isSensitiveInput={isSensitiveInput}
        withEmoji={false}
        withFooter={footer?.showFooter}
        onKeyDown={handleKeyDown}
        onSubmit={handleSubmit}
      >
        {files.length > maxFiles && (
          <SMaxFilesError>
            <SNotificationWarningIcon size="20" color={colors.COLOR_RED_800} />
            <span>{t('footer.maxFilesError', { maxFiles })}</span>
          </SMaxFilesError>
        )}
        {files.length > 0 && (
          <SFilesContainer>
            {files.map(file => {
              const errorMessage = getFileError(file);
              return (
                <FilePreview
                  key={file.name}
                  data={{
                    file_name: file.name,
                    file_size: file.size,
                    file_type: file.type,
                  }}
                  resetFile={() => resetFile(file.name)}
                  status={
                    errorMessage ? MessageStatus.ERROR : MessageStatus.PENDING
                  }
                  errorMessage={errorMessage}
                  fileWidth={fileWidth}
                />
              );
            })}
          </SFilesContainer>
        )}

        <SFormInnerContainer>
          <label htmlFor="chat-text-input" aria-label={t('footer.textInput')}>
            <SHiddenLabel>{t('footer.textInput')}</SHiddenLabel>
          </label>

          {isSensitiveInput ? (
            <SInput
              data-test="sensitive-input-field"
              as="input"
              id="chat-text-input"
              ref={(inputRef as unknown) as React.RefObject<HTMLInputElement>}
              name="utterance"
              type={isRevealed ? 'text' : 'password'}
              isIOS={isIOS}
              isSensitiveInput
              placeholder={t('footer.sensitivePlaceholder')}
              value={utterance}
              disabled={isInputDisabled}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              onChange={handleInputChange}
            />
          ) : (
            <SInput
              data-test="input-field"
              id="chat-text-input"
              ref={inputRef}
              name="utterance"
              isIOS={isIOS}
              placeholder={t('footer.placeholder')}
              value={utterance}
              disabled={isInputDisabled}
              autoComplete="off"
              onChange={handleInputChange}
            />
          )}

          {isSensitiveInput ? (
            <SRevealButton
              data-test="reveal-button-input"
              type="button"
              onClick={toggleReveal}
              aria-label={t(
                isRevealed ? 'footer.hideInput' : 'footer.revealInput',
              )}
              aria-pressed={isRevealed}
            >
              {isRevealed ? (
                <SEyeSlashIcon primaryColor={colors.COLOR_GREY_900} size="20" />
              ) : (
                <SEyeIcon primaryColor={colors.COLOR_GREY_900} size="20" />
              )}
            </SRevealButton>
          ) : (
            <SAttachLabel
              aria-label={t('footer.attachFile')}
              {...(!isSafari &&
                attachmentsEnabled && {
                  onMouseDown: handleFileUpload,
                })}
            >
              {attachmentsEnabled && (
                <SAttachIcon primaryColor={colors.COLOR_GREY_800} size="20" />
              )}
              {!isAttachmentsDisabled && (
                <input
                  {...fileInputProps({
                    ...(isSafari && { onClick: handleFileUpload }),
                    disabled: isAttachmentsDisabled,
                  })}
                  style={{ display: 'none' }}
                />
              )}
            </SAttachLabel>
          )}
          <IconSend
            color={sendButtonColor}
            disabled={isSendButtonDisabled}
            handleClick={handleSubmit}
          />
        </SFormInnerContainer>
      </SForm>
      <FooterText
        showFooter={footer?.showFooter}
        text={footer?.text}
        logoURL={footer?.logoURL}
      />
    </SFooter>
  );
};

export default ChatFooter;
